/**
 * LMS Staff Permissions admin (plan §7, Phase 10C).
 *
 * SUPERUSER ONLY. The user's requirement was explicit: "only Superusers can
 * allot permissions to staff, staff cannot change anything about permissions,
 * separate view-only permission, each tab has its own permission".
 *
 * Enforcement lives in three independent places, and this screen is the WEAKEST
 * of them (a convenience UI, never the boundary):
 *   1. Backend — every endpoint here is `permission_classes = [IsSuperUser]`
 *      (backend/lms/views_staff_permissions.py), so a non-superuser gets 403
 *      regardless of what the SPA renders.
 *   2. Sidebar — the menu entry is `isSuperuserOnly: true` in menuConfig.ts.
 *   3. This component — renders a refusal panel unless `user.is_superuser`.
 *
 * VIEW and EDIT are stored as two independent booleans on one
 * LmsStaffPermission row per (user, tab). "View-only" is therefore
 * `can_view=true, can_edit=false`. Granting Edit without View would produce a
 * tab the operator can mutate but not see, so the checkbox handler couples them
 * one way only: ticking Edit implies View; unticking View clears Edit. That is a
 * UI affordance — the backend stores exactly what it is sent, and
 * HasLmsTabPermission checks `can_view` for GETs and `can_edit` for writes
 * separately.
 *
 * Endpoints:
 *   GET  /api/lms/admin/staff_permissions/by_user/<id>/  (full 6-row array,
 *        already defaulting absent tabs to false/false — no client-side merge)
 *   POST /api/lms/admin/staff_permissions/set/           (one tab at a time)
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldAlert, ShieldCheck, Search, RefreshCcw, Loader2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../adminApi';
import lmsStaffService, {
    LmsStaffPermissionByUserRow,
    LmsTabKey,
    getLmsApiErrorMessage,
} from './lmsStaffApi';
import { formatDateTime } from './formatters';

/** Subset of /api/public/users/ that this screen needs. */
interface StaffUser {
    id: number;
    username: string;
    first_name?: string;
    last_name?: string;
    is_staff: boolean;
    is_superuser: boolean;
    user_type?: string;
}

const LmsPermissionsAdmin: React.FC = () => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const [filter, setFilter] = React.useState('');
    const [selectedUserId, setSelectedUserId] = React.useState<number | null>(null);
    /** Tab currently being saved, so only that row shows a spinner. */
    const [savingTab, setSavingTab] = React.useState<LmsTabKey | null>(null);

    const isSuperuser = !!user?.is_superuser;

    const { data: users, isLoading: usersLoading } = useQuery({
        queryKey: ['admin-users'],
        queryFn: async () => {
            const response = await adminService.getUsers();
            return response.data as StaffUser[];
        },
        enabled: isSuperuser,
    });

    const permissionsKey = ['lms-staff-permissions-by-user', selectedUserId];

    const {
        data: permissions,
        isLoading: permissionsLoading,
        isError: permissionsError,
        error: permissionsErrorObj,
        refetch: refetchPermissions,
    } = useQuery({
        queryKey: permissionsKey,
        queryFn: async () => {
            const response = await lmsStaffService.getStaffPermissionsByUser(
                selectedUserId as number
            );
            return response.data;
        },
        enabled: isSuperuser && selectedUserId !== null,
    });

    const setPermissionMutation = useMutation({
        mutationFn: (vars: { tab: LmsTabKey; can_view: boolean; can_edit: boolean }) =>
            lmsStaffService.setStaffPermission({
                user_id: selectedUserId as number,
                tab: vars.tab,
                can_view: vars.can_view,
                can_edit: vars.can_edit,
            }),
        onSuccess: (response) => {
            toast.success(
                `Updated ${response.data.tab}: view=${response.data.can_view}, edit=${response.data.can_edit}`
            );
            queryClient.invalidateQueries({ queryKey: permissionsKey });
            queryClient.invalidateQueries({ queryKey: ['lms-staff-permissions-list'] });
            // The target user's own hub reads my_permissions; drop that cache so
            // a same-browser session picks the change up on next mount.
            queryClient.invalidateQueries({ queryKey: ['lms-staff-my-permissions'] });
        },
        onError: (error: unknown) => {
            toast.error(getLmsApiErrorMessage(error, 'Failed to update the permission'));
        },
        onSettled: () => setSavingTab(null),
    });

    const applyChange = (
        row: LmsStaffPermissionByUserRow,
        field: 'can_view' | 'can_edit',
        checked: boolean
    ) => {
        let can_view = row.can_view;
        let can_edit = row.can_edit;

        if (field === 'can_view') {
            can_view = checked;
            // Edit without View would be an invisible-but-mutable tab.
            if (!checked) can_edit = false;
        } else {
            can_edit = checked;
            if (checked) can_view = true;
        }

        setSavingTab(row.tab);
        setPermissionMutation.mutate({ tab: row.tab, can_view, can_edit });
    };

    if (!isSuperuser) {
        return (
            <div className="w-full rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
                <ShieldAlert className="mx-auto h-8 w-8 text-amber-500" />
                <p className="mt-3 text-sm font-medium text-amber-800">
                    Only superusers can allot LMS staff permissions.
                </p>
                <p className="mt-1 text-sm text-amber-700">
                    Staff accounts cannot view or change permission assignments.
                </p>
            </div>
        );
    }

    const needle = filter.trim().toLowerCase();
    const candidateUsers = (users ?? [])
        .filter((candidate) => candidate.is_staff || candidate.is_superuser)
        .filter(
            (candidate) =>
                !needle ||
                candidate.username.toLowerCase().includes(needle) ||
                `${candidate.first_name ?? ''} ${candidate.last_name ?? ''}`
                    .toLowerCase()
                    .includes(needle)
        );

    const selectedUser = (users ?? []).find(
        (candidate) => candidate.id === selectedUserId
    );

    return (
        <div className="w-full space-y-4">
            <div>
                <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                    <ShieldCheck className="h-6 w-6 text-intellend-green" />
                    LMS Staff Permissions
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                    Grant View and Edit independently, per tab, per staff user. Superusers
                    always have full access and need no rows here.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {/* ── Staff user picker ──────────────────────────────────── */}
                <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                            placeholder="Search staff users..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {usersLoading ? (
                        <div className="space-y-2">
                            <Skeleton className="h-9 w-full" />
                            <Skeleton className="h-9 w-full" />
                            <Skeleton className="h-9 w-full" />
                        </div>
                    ) : candidateUsers.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-500">
                            No staff users match this filter.
                        </p>
                    ) : (
                        <ul className="max-h-[28rem] divide-y divide-gray-100 overflow-y-auto">
                            {candidateUsers.map((candidate) => (
                                <li key={candidate.id}>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedUserId(candidate.id)}
                                        className={[
                                            'flex w-full items-center justify-between gap-2 px-2 py-2 text-left text-sm',
                                            candidate.id === selectedUserId
                                                ? 'rounded-md bg-intellend-green/10 font-medium text-gray-900'
                                                : 'text-gray-700 hover:bg-gray-50',
                                        ].join(' ')}
                                    >
                                        <span className="min-w-0 truncate">
                                            {candidate.username}
                                            {(candidate.first_name || candidate.last_name) && (
                                                <span className="ml-1 text-xs text-gray-400">
                                                    {candidate.first_name} {candidate.last_name}
                                                </span>
                                            )}
                                        </span>
                                        {candidate.is_superuser && (
                                            <Badge variant="outline" className="shrink-0 text-xs">
                                                superuser
                                            </Badge>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* ── Per-tab View/Edit grid ─────────────────────────────── */}
                <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:col-span-2">
                    {selectedUserId === null ? (
                        <p className="py-20 text-center text-sm text-gray-500">
                            Select a staff user to review and change their LMS tab
                            permissions.
                        </p>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h3 className="text-sm font-semibold text-gray-800">
                                    Tab permissions for{' '}
                                    <span className="font-mono">
                                        {selectedUser?.username ?? `user #${selectedUserId}`}
                                    </span>
                                </h3>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => refetchPermissions()}
                                    className="gap-2"
                                >
                                    <RefreshCcw className="h-4 w-4" /> Refresh
                                </Button>
                            </div>

                            {selectedUser?.is_superuser && (
                                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">
                                    This account is a superuser and already has implicit full
                                    View + Edit access to every tab, regardless of the rows
                                    below.
                                </div>
                            )}

                            {permissionsError ? (
                                <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
                                    {getLmsApiErrorMessage(
                                        permissionsErrorObj,
                                        'Unable to load this user\u2019s permissions.'
                                    )}
                                </div>
                            ) : permissionsLoading ? (
                                <Skeleton className="h-56 w-full" />
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                                            <tr>
                                                <th className="px-3 py-2">Tab</th>
                                                <th className="px-3 py-2 text-center">View</th>
                                                <th className="px-3 py-2 text-center">Edit</th>
                                                <th className="px-3 py-2">Last Updated</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(permissions ?? []).map((row) => {
                                                const busy =
                                                    savingTab === row.tab &&
                                                    setPermissionMutation.isPending;
                                                return (
                                                    <tr
                                                        key={row.tab}
                                                        className="border-t border-gray-100"
                                                    >
                                                        <td className="px-3 py-2">
                                                            <span className="font-medium text-gray-800">
                                                                {row.tab_label}
                                                            </span>
                                                            <p className="font-mono text-xs text-gray-400">
                                                                {row.tab}
                                                            </p>
                                                        </td>
                                                        <td className="px-3 py-2 text-center">
                                                            <Checkbox
                                                                checked={row.can_view}
                                                                disabled={busy}
                                                                aria-label={`View ${row.tab}`}
                                                                onCheckedChange={(checked) =>
                                                                    applyChange(
                                                                        row,
                                                                        'can_view',
                                                                        checked === true
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-center">
                                                            <Checkbox
                                                                checked={row.can_edit}
                                                                disabled={busy}
                                                                aria-label={`Edit ${row.tab}`}
                                                                onCheckedChange={(checked) =>
                                                                    applyChange(
                                                                        row,
                                                                        'can_edit',
                                                                        checked === true
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-gray-500">
                                                            {busy ? (
                                                                <span className="flex items-center gap-1.5">
                                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                                    Saving...
                                                                </span>
                                                            ) : (
                                                                formatDateTime(row.updated_at)
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <p className="text-xs text-gray-400">
                                View-only access is <strong>View ticked, Edit unticked</strong>.
                                Ticking Edit automatically grants View; removing View also
                                removes Edit. Every change is written to the LMS audit log with
                                its old and new value.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LmsPermissionsAdmin;
